import { EnrollementQuestionnaireSchema } from '../../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import Form from './Form'
import { MsPlanDetails } from '../../interface/enrollmentApplication/MsPlanDetails'
import { PulseLoader } from 'react-spinners'
import React from 'react'
import { fetchAdditionalQuestionnaireSchema } from '../../service/quotes'
import styled from '@emotion/styled'
import theme from '../../global/theme'

interface EnrollmentQuestionnaireProps {
  planId: string
  forSpouse: boolean
  onSuccess: (enrollmentApplicationKey: string) => void
  onError: (error: string) => void
  onLiteSessionUpgraded?: () => void
  planDetails: MsPlanDetails
}

const Container = styled.div`
  padding: 20px;
  @media screen and (max-width: 750px) {
    padding: 0px;
  }
`

const EnrollmentQuestionnaire: React.FC<EnrollmentQuestionnaireProps> = ({
  planId,
  forSpouse,
  onSuccess,
  onError,
  onLiteSessionUpgraded,
  planDetails,
}) => {
  const [loading, setLoading] = React.useState(true)
  const [questionnaireData, setQuestionnaireData] =
    React.useState<EnrollementQuestionnaireSchema>()

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await fetchAdditionalQuestionnaireSchema(
          planId,
          forSpouse
        )
        if (response.status !== 'success' || response.data.length === 0) {
          throw new Error('Failed to fetch questionnaire data')
        }
        const questionnaireSchema = response.data[0]

        if (questionnaireSchema.sections) {
          questionnaireSchema.sections.forEach((section) => {
            section.body.forEach((field) => {
              // Modify eft_withdraw_day field options to limit to [1-15] instead of [1-28]
              if (field.id === 'eft_withdraw_day' && field.options) {
                field.options = field.options.filter((option) => {
                  const value =
                    typeof option === 'number' ? option : option.value
                  return value >= 1 && value <= 15
                })
              }

              // make ssn question required
              if (
                field.id === 'medicare_information_ssn' ||
                field.id === 'ssn'
              ) {
                field.required = true
              }

              // make email and phone required
              if (
                field.id === 'applicant_email' ||
                field.id === 'applicant_phone'
              ) {
                field.required = true
              }
            })
          })
        }

        setQuestionnaireData(questionnaireSchema)
      } catch (error: any) {
        onError && onError('Something went wrong')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [forSpouse, planId])

  if (loading) {
    return (
      <Container>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <PulseLoader size={15} color={theme.colors.primary} loading={true} />
        </div>
      </Container>
    )
  }

  if (questionnaireData) {
    return (
      <Container>
        <Form
          questionnaireData={questionnaireData}
          onSuccess={onSuccess}
          onError={onError}
          onLiteSessionUpgraded={onLiteSessionUpgraded}
          planId={planId}
          forSpouse={forSpouse}
          loading={loading}
          setLoading={setLoading}
          planDetails={planDetails}
        />
      </Container>
    )
  }

  return null
}

export default EnrollmentQuestionnaire
